import { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({
  breaks: true,
  gfm: true,
});

const addTargetBlankToLinks = (html: string): string => {
  return html.replace(
    /<a\s+href="/g,
    '<a target="_blank" rel="noopener noreferrer" href="'
  );
};

const PHONE_CANDIDATE_REGEX =
  /(?:\+1[\s-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}/g;
const EXAMPLE_PHONE_REGEX = /^\D*555\D*555\D*5555\D*$/;
const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;
const MAX_INPUT_LENGTH = 50_000;
const MAX_CANDIDATE_LENGTH = 20;

const isValidUSPhone = (candidate: string): boolean => {
  if (candidate.length > MAX_CANDIDATE_LENGTH) return false;
  const digits = candidate.replace(/\D/g, "");

  if (digits.length !== 10 && digits.length !== 11) return false;

  let areaCode: string;
  let exchange: string;

  if (digits.length === 10) {
    if (digits.startsWith("0") || digits.startsWith("1")) return false;
    areaCode = digits.slice(0, 3);
    exchange = digits.slice(3, 6);
  } else if (digits.length === 11 && digits.startsWith("1")) {
    if (digits.startsWith("10") || digits.startsWith("11")) return false;
    areaCode = digits.slice(1, 4);
    exchange = digits.slice(4, 7);
  } else {
    return false;
  }

  if (areaCode.slice(1).startsWith("11")) return false;
  if (exchange.slice(1).startsWith("11")) return false;
  if (areaCode.startsWith("9")) return false;

  return !EXAMPLE_PHONE_REGEX.test(candidate);
};

const convertPhoneNumbersToLinks = (html: string): string => {
  if (html.length > MAX_INPUT_LENGTH) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const body = doc.body;

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode(node: Text): number {
        let parent = node.parentElement;
        while (parent && parent !== body) {
          if (parent.tagName === "A") return NodeFilter.FILTER_REJECT;
          parent = parent.parentElement;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }

    for (const textNode of textNodes) {
      const text = textNode.textContent ?? "";
      PHONE_CANDIDATE_REGEX.lastIndex = 0;
      const matches: Array<{ match: string; index: number }> = [];
      let m: RegExpExecArray | null;
      while ((m = PHONE_CANDIDATE_REGEX.exec(text)) !== null) {
        if (isValidUSPhone(m[0])) {
          matches.push({ match: m[0], index: m.index });
        }
      }
      if (matches.length === 0) continue;

      const fragment = doc.createDocumentFragment();
      let lastIndex = 0;
      for (const { match, index } of matches) {
        if (index > lastIndex) {
          fragment.appendChild(
            doc.createTextNode(text.slice(lastIndex, index))
          );
        }
        const digits = match.replace(/\D/g, "");
        const anchor = doc.createElement("a");
        anchor.href = `tel:${digits}`;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.textContent = match;
        fragment.appendChild(anchor);
        lastIndex = index + match.length;
      }
      if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
      }
      textNode.parentNode!.replaceChild(fragment, textNode);
    }

    return DOMPurify.sanitize(body.innerHTML, {
      ADD_ATTR: ["href", "target", "rel"],
      ALLOWED_URI_REGEXP,
    });
  } catch {
    return html;
  }
};

const hasMarkdownSyntax = (text: string): boolean => {
  const markdownPatterns = [
    /^#{1,6}\s/m,
    /\*\*.*?\*\*/,
    /\*.*?\*/,
    /~~.*?~~/,
    /```[\s\S]*?```/,
    /`[^`]+`/,
    /^\s{0,10}[-*+]\s/m,
    /^\s{0,10}\d{1,3}\.\s/m,
    /\[[^\]]{0,100}]\([^)]{0,200}\)/,
    /^>\s/m,
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
};

export const useMarkdownToHtml = (text: string): string => {
  return useMemo(() => {
    const cleanText = text
      .replace(/<br>/g, "\n")
      .replace(/```markdown\n([\s\S]*?)\n```/g, "$1")
      .replace(/```\n```/g, "");

    const sanitizeConfig: DOMPurify.Config = {
      ADD_ATTR: ["target", "rel"],
      ALLOWED_URI_REGEXP,
    };

    if (!hasMarkdownSyntax(cleanText)) {
      const htmlText = cleanText.replace(/\n/g, "<br>");
      const htmlWithTargetBlank = addTargetBlankToLinks(htmlText);
      const htmlWithPhoneLinks =
        convertPhoneNumbersToLinks(htmlWithTargetBlank);
      return DOMPurify.sanitize(htmlWithPhoneLinks, sanitizeConfig);
    }

    const rawHtml = marked.parse(cleanText) as string;
    const htmlWithTargetBlank = addTargetBlankToLinks(rawHtml);
    const htmlWithPhoneLinks = convertPhoneNumbersToLinks(htmlWithTargetBlank);
    return DOMPurify.sanitize(htmlWithPhoneLinks, sanitizeConfig);
  }, [text]);
};
